import StarBorderIcon from '@mui/icons-material/PhotoTwoTone'
import DownloadIcon from '@mui/icons-material/Download'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import {
  Box,
  Button,
  IconButton,
  ImageList,
  ImageListItem,
  MenuItem,
  Select,
  Typography,
  Paper,
} from '@mui/material'
import { PhotoPosition, VehiclePhoto } from '@otw/models'
import { updatePhotoPosition } from 'core/inventory/updatePhotoPosition'
import React, { useState } from 'react'
import { useSnackbar } from '@/hooks/common/useSnackbar'
import { isVideoFileName } from '@/components/inventory/utils/inventoryMedia'

interface ViewPhotosTabProps {
  collectionName: string
  vehicleId: string
  photos: VehiclePhoto[] | undefined
  onDeleteSelectedPhotos: (selectedPhotos: VehiclePhoto[]) => void
  onDownloadSelectedPhotos?: (selectedPhotos: VehiclePhoto[]) => Promise<void>
  isDownloadingSelected?: boolean
  updatePosition?: (params: { name: string; position: PhotoPosition }) => void
  isMobile?: boolean
}

const vehiclePhotoPositons = ['main', 'right', 'left', 'back', 'interior', 'box']

const ViewPhotosTab: React.FC<ViewPhotosTabProps> = ({
  collectionName,
  vehicleId: rowId,
  photos: initialPhotos,
  onDeleteSelectedPhotos,
  onDownloadSelectedPhotos,
  isDownloadingSelected = false,
  updatePosition,
  isMobile = false,
}) => {
  const [selectedPhotos, setSelectedPhotos] = useState<VehiclePhoto[]>([])
  const [optimisticPhotos, setOptimisticPhotos] = useState<VehiclePhoto[] | undefined>(initialPhotos)
  const { showSuccess, showError, showInfo, showSnackbar, showWarning } = useSnackbar()

  // Keep optimisticPhotos in sync with initialPhotos when they change from outside
  React.useEffect(() => {
    setOptimisticPhotos(initialPhotos)
  }, [initialPhotos])

  const photos = optimisticPhotos || []

  const handleSelectPhoto = (photo: VehiclePhoto) => {
    if (selectedPhotos.includes(photo)) {
      setSelectedPhotos(prevState => prevState.filter(p => p.url !== photo.url))
      return
    }

    setSelectedPhotos(prevState => [...prevState, photo])
  }

  const handleSetPhotoPosition = (newPosition: string, item: VehiclePhoto) => {
    // Optimistically update the photo position in the UI
    const updatedPhotos = photos.map(photo => {
      // Clear the same position from any other photo if it's 'main'
      if (newPosition === 'main' && photo.position === 'main') {
        return { ...photo, position: undefined }
      }

      // Update the selected photo with the new position
      if (photo.url === item.url) {
        return { ...photo, position: newPosition as PhotoPosition }
      }

      return photo
    })

    setOptimisticPhotos(updatedPhotos)

    // Also update the selected photos array
    setSelectedPhotos(prev =>
      prev.map(photo => (photo.url === item.url ? { ...photo, position: newPosition as PhotoPosition } : photo)),
    )

    // Call the Firestore update
    try {
      if (updatePosition) {
        updatePosition({
          name: item.name,
          position: newPosition as PhotoPosition,
        })
        showSuccess(`Photo position updated to ${newPosition}`)
      } else {
        // Fallback to the original method if updatePosition isn't provided
        updatePhotoPosition(collectionName, rowId, item.name, newPosition as PhotoPosition)
          .then(() => {
            showSuccess(`Photo position updated to ${newPosition}`)
          })
          .catch(error => {
            showError('Error updating photo position')
            console.error('Error updating photo position:', error)
            // Revert the optimistic update on error
            setOptimisticPhotos(initialPhotos)
          })
      }
    } catch (error) {
      showError('Error updating photo position')
      console.error('Error updating photo position:', error)
      // Revert the optimistic update on error
      setOptimisticPhotos(initialPhotos)
    }
  }

  const handleDownloadSelected = () => {
    if (selectedPhotos.length > 0 && onDownloadSelectedPhotos) {
      onDownloadSelectedPhotos(selectedPhotos)
    }
  }

  const handleOpenInNewTab = (url: string, e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation() // Prevent item selection when clicking the icon
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  return (
    <>
      {selectedPhotos.length > 0 && (
        <>
          <Typography variant='h6' sx={{ mb: 1 }}>
            {selectedPhotos.length} Photos Selected
          </Typography>

          <Box sx={{ display: 'flex' }}>
            <Button
              variant='outlined'
              sx={{ mr: 1 }}
              startIcon={<DownloadIcon />}
              onClick={handleDownloadSelected}
              disabled={isDownloadingSelected || !onDownloadSelectedPhotos}
            >
              {isDownloadingSelected ? 'Downloading...' : 'Download Photos'}
            </Button>
            <Button variant='outlined' color='error' onClick={() => onDeleteSelectedPhotos(selectedPhotos)}>
              Delete Photos
            </Button>
          </Box>
        </>
      )}

      <ImageList
        sx={{
          width: '100%',
          height: isMobile ? 'auto' : 480,
          ...(isMobile && { mb: 0 }),
        }}
        cols={isMobile ? 2 : 6}
        rowHeight={isMobile ? 220 : 150}
        gap={isMobile ? 12 : 8}
      >
        {photos && photos.length > 0 ? (
          photos.map(photo => (
            <ImageListItem key={photo.url} onClick={() => handleSelectPhoto(photo)}>
              {/* Custom photo container with absolute positioning for controls */}
              <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
                {isVideoFileName(photo.name) ? (
                  <video
                    src={photo.url}
                    muted
                    playsInline
                    preload='metadata'
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : (
                  <img
                    srcSet={
                      isMobile
                        ? `${photo.url}?w=320&h=320&fit=crop&auto=format&dpr=2 2x`
                        : `${photo.url}?w=150&h=150&fit=crop&auto=format&dpr=2 2x`
                    }
                    src={
                      isMobile
                        ? `${photo.url}?w=320&h=320&fit=crop&auto=format`
                        : `${photo.url}?w=150&h=150&fit=crop&auto=format`
                    }
                    alt={photo.name}
                    loading='lazy'
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                )}

                {/* Position indicator badge for main photos */}
                {photo.position === 'main' && (
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 4,
                      right: 4,
                      backgroundColor: 'primary.main',
                      color: 'white',
                      borderRadius: '4px',
                      padding: '2px 6px',
                      fontSize: '0.7rem',
                      fontWeight: 'bold',
                      zIndex: 2,
                    }}
                  >
                    MAIN
                  </Box>
                )}

                {/* Open in new tab button - always visible in top right */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    zIndex: 2,
                  }}
                >
                  <IconButton
                    size='small'
                    sx={{
                      color: 'white',
                      backgroundColor: 'rgba(0,0,0,0.4)',
                      '&:hover': { backgroundColor: 'rgba(0,0,0,0.6)' },
                    }}
                    onClick={e => handleOpenInNewTab(photo.url, e)}
                  >
                    <OpenInNewIcon fontSize='small' />
                  </IconButton>
                </Box>

                {/* Selection overlay */}
                {selectedPhotos.includes(photo) && (
                  <>
                    {/* Top gradient overlay for selected photos */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '80px',
                        background:
                          'linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 70%, rgba(0,0,0,0) 100%)',
                        zIndex: 1,
                      }}
                    />

                    {/* Position selector - only shown when selected, on left side */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 4,
                        left: 4,
                        zIndex: 2,
                      }}
                    >
                      <Select
                        value={photo.position || ''}
                        onChange={e => handleSetPhotoPosition(e.target.value, photo)}
                        displayEmpty
                        size='small'
                        inputProps={{ 'aria-label': 'Vehicle Position' }}
                        sx={{
                          color: 'white',
                          '.MuiSelect-icon': { color: 'white' },
                          backgroundColor: 'rgba(0,0,0,0.4)',
                          '&:hover': { backgroundColor: 'rgba(0,0,0,0.6)' },
                        }}
                      >
                        <MenuItem value=''>Select position</MenuItem>
                        {vehiclePhotoPositons.map(position => (
                          <MenuItem key={position} value={position}>
                            {position}
                          </MenuItem>
                        ))}
                      </Select>
                    </Box>

                    {/* Bottom gradient overlay and indicator for selected photos */}
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: '50px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '0 8px',
                        background:
                          'linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 70%, rgba(0,0,0,0) 100%)',
                        zIndex: 1,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <IconButton size='small' sx={{ color: 'white' }}>
                          <StarBorderIcon fontSize='small' />
                        </IconButton>
                        <Typography variant='body2' sx={{ color: 'white', ml: 1 }}>
                          {photo.position || ''}
                        </Typography>
                      </Box>
                    </Box>
                  </>
                )}
              </Box>
            </ImageListItem>
          ))
        ) : (
          <Typography>No photos for this vehicle</Typography>
        )}
      </ImageList>
    </>
  )
}

export default ViewPhotosTab
